import {TKey} from '@/translations/i18n'
import {
    ActivityReportSector,
    ActivityReportStatus,
    ActivityReportsColumn
} from '@/features/accesses/activity-report/types'

export const activityReportSectorToLabel = {
    outpatient: 'activity_report:sector_outpatient',
    domiciliary: 'activity_report:sector_domiciliary'
} as const satisfies Record<ActivityReportSector, TKey>

export const activityReportStatusToLabel = {
    pending: 'activity_report:status_pending',
    billed: 'activity_report:status_billed'
} as const satisfies Record<ActivityReportStatus, TKey>

export const activityReportStatusToBadgeVariant = {
    pending: 'orange',
    billed: 'success'
} as const satisfies Record<ActivityReportStatus, 'orange' | 'success'>

export const activityReportColumnToLabel = {
    period: 'activity_report:column_period',
    sector: 'activity_report:column_sector',
    accesses: 'activity_report:column_accesses',
    absences: 'activity_report:column_absences',
    status: 'activity_report:column_status',
    file: 'activity_report:column_file',
    createdAtAndAuthor: 'activity_report:column_created_at',
    moreActions: 'commons:actions'
} as const satisfies Record<ActivityReportsColumn, TKey>

// Sector select options (key → label) for the extraction dialog.
export const activityReportSectorOptions = activityReportSectorToLabel

// Backend business error code → user-facing message (FR-029). Raw codes are never shown.
const ACTIVITY_REPORT_ERROR_LABELS: Record<string, TKey> = {
    ACCESSES_NOT_VALIDATED: 'activity_report:error_accesses_not_validated',
    NO_BILLABLE_ACCESSES: 'activity_report:error_no_billable_accesses',
    ALREADY_BILLED: 'activity_report:error_already_billed',
    INVALID_DATE_RANGE: 'activity_report:error_invalid_date_range',
    'activity-report-not-found': 'activity_report:error_not_found'
}

export const resolveActivityReportErrorLabel = (code: string | undefined): TKey =>
    (code ? ACTIVITY_REPORT_ERROR_LABELS[code] : undefined) ?? 'errors:default'

// File (export job) status → label for the history "File" column.
export const activityReportFileStatusToLabel = (status: string | undefined | null): TKey => {
    switch (status) {
        case 'pending':
            return 'activity_report:file_status_pending'
        case 'processing':
            return 'activity_report:file_status_processing'
        case 'ready':
            return 'activity_report:file_status_ready'
        case 'failed':
            return 'activity_report:file_status_failed'
        default:
            return 'activity_report:file_status_none'
    }
}

export const activityReportFileStatusToBadgeVariant = (
    status: string | undefined | null
): 'danger' | 'neutral' | 'orange' | 'success' => {
    switch (status) {
        case 'ready':
            return 'success'
        case 'failed':
            return 'danger'
        case 'processing':
            return 'orange'
        default:
            return 'neutral'
    }
}

// Download is offered only for a ready file; regenerate when the job is missing or in a
// terminal non-ready state (FR-022 / FR-023).
export const canDownloadActivityReportFile = (status: string | undefined | null): boolean => status === 'ready'

export const canRegenerateActivityReportFile = (status: string | undefined | null): boolean =>
    !status || status === 'failed' || status === 'expired' || status === 'archived' || status === 'cancelled'
